import React from "react";
import { Box, Container, Typography, Grid, IconButton, Divider, Stack, Link as MuiLink, alpha } from "@mui/material";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import YouTubeIcon from '@mui/icons-material/YouTube';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerSections = [
    {
      title: "Quick Links",
      links: [
        { name: "Home", path: "/" },
        { name: "Collection", path: "/collection" },
        { name: "About Us", path: "/about" },
        { name: "Sell Your Coins", path: "/sell" },
        { name: "Contact", path: "/contact" },
      ],
    },
    {
      title: "Resources",
      links: [
        { name: "Coin Grading Guide", path: "/grading-guide" },
        { name: "Investment Guide", path: "/investment-guide" },
        { name: "FAQ", path: "/faq" },
        { name: "Authentication", path: "/authentication" },
        { name: "Privacy Policy", path: "/privacy" },
      ],
    },
  ];

  const locations = [
    {
      country: "UK",
      flag: "🇬🇧",
      address: "5 Lawn Road\nEastleigh, England SO50 4GT",
      phone: "+44 20 7946 0100",
      whatsappNumber: "442079460100",
      email: "uk@diamondcoin.com",
      hours: "Mon-Fri: 9am - 6pm GMT",
      whatsapp: true
    },
    {
      country: "CA",
      flag: "🇨🇦",
      address: "158 Barkley Terrace\nVictoria, British Columbia, Canada, V8S-2J6",
      phone: "+1-250-883-5000",
      whatsappNumber: "12508835000",
      email: "canada@diamondcoin.com",
      hours: "Mon-Fri: 9am - 6pm PST",
      whatsapp: true
    },
    {
      country: "US",
      flag: "🇺🇸",
      companyName: "Diamond Coin Corporation",
      address: "1100 NW Northwood Road\nSeattle, Washington State, 98177",
      phone: "+1 206-363-9645",
      whatsappNumber: "+12063639645",
      email: "usa@diamondcoin.com",
      hours: "Mon-Fri: 9am - 6pm PST",
      whatsapp: true
    },
    {
      country: "NZ/AU",
      flag: "🇳🇿",
      address: "14, 19 Lantana Avenue\nAuckland, New Zealand, 0630",
      phone: "+64 21 1983352",
      whatsappNumber: "64211983352",
      email: "nz@diamondcoin.com",
      hours: "Mon-Fri: 9am - 6pm NZST",
      whatsapp: true
    },
    {
      country: "RSA",
      flag: "🇿🇦",
      address: "341 Beyers Naude Drive, Northcliff Johannesburg, South Africa, 2195",
      phone: "+27720100332",
      whatsappNumber: "27720100332",
      email: "sa@diamondcoin.com",
      hours: "Mon-Fri: 9am - 6pm SAST",
      whatsapp: true
    }
  ];

  // Email routing configuration - using standard Concierge email
  const primaryEmail = "Concierge@DiamondCoin.us";

  const handleWhatsAppClick = (whatsappNumber) => {
    window.open(`https://wa.me/${whatsappNumber}`, '_blank');
  };

  const handleEmailClick = (e, locationEmail) => {
    e.preventDefault();
    window.location.href = `mailto:${primaryEmail}?subject=Inquiry from ${locationEmail}`;
  };

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: '#0A0A0A',
        color: '#FFFFFF',
        position: 'relative',
        overflow: 'hidden',
        borderTop: `1px solid ${alpha('#C6A75E', 0.2)}`,
      }}
    >
      {/* Animated gold line at top */}
      <Box
        component={motion.div}
        animate={{
          x: ['-100%', '100%'],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'linear',
        }}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '2px',
          background: 'linear-gradient(90deg, transparent, #C6A75E, #C6A75E, transparent)',
        }}
      />

      <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 }, position: 'relative', zIndex: 2 }}>
        <Grid container spacing={4}>
          {/* Brand Section */}
          <Grid item xs={12} md={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #C6A75E 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  fontFamily: '"Cormorant Garamond", serif',
                }}
              >
                DiamondCoin
              </Typography>
              
              {/* Diamond icon added near the top of US/UK section - animated diamond */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <motion.div
                  animate={{ 
                    rotate: 360,
                    scale: [1, 1.1, 1]
                  }}
                  transition={{ 
                    rotate: { duration: 8, repeat: Infinity, ease: "linear" },
                    scale: { duration: 2, repeat: Infinity, ease: "easeInOut" }
                  }}
                >
                  <Typography sx={{ fontSize: '1.5rem' }}>💎</Typography>
                </motion.div>
                <Typography variant="body2" sx={{ color: '#C6A75E', fontWeight: 500 }}>
                  Premium Investment Coins
                </Typography>
              </Box>
              
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 3, lineHeight: 1.6 }}>
                Your trusted partner in rare and investment-grade coins since 2005. 
                Authenticity guaranteed with every purchase.
              </Typography>
              
              {/* Social Links */}
              <Stack direction="row" spacing={1.5}>
                {[FacebookIcon, TwitterIcon, InstagramIcon, LinkedInIcon, YouTubeIcon].map((Icon, index) => (
                  <IconButton
                    key={index}
                    sx={{
                      color: '#FFFFFF',
                      backgroundColor: alpha('#C6A75E', 0.1),
                      '&:hover': {
                        backgroundColor: '#C6A75E',
                        color: '#000000',
                        transform: 'translateY(-3px)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <Icon fontSize="small" />
                  </IconButton>
                ))}
              </Stack>
            </motion.div>
          </Grid>

          {/* Quick Links Sections */}
          {footerSections.map((section, idx) => (
            <Grid item xs={12} sm={6} md={2} key={idx}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                viewport={{ once: true }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    fontWeight: 600,
                    color: '#C6A75E',
                    fontSize: '1rem',
                    letterSpacing: '0.05em',
                  }}
                >
                  {section.title}
                </Typography>
                <Stack spacing={1.5}>
                  {section.links.map((link, linkIdx) => (
                    <MuiLink
                      key={linkIdx}
                      component={Link}
                      to={link.path}
                      sx={{
                        color: 'rgba(255,255,255,0.7)',
                        textDecoration: 'none',
                        fontSize: '0.9rem',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          color: '#C6A75E',
                          transform: 'translateX(5px)',
                        },
                      }}
                    >
                      {link.name}
                    </MuiLink>
                  ))}
                </Stack>
              </motion.div>
            </Grid>
          ))}

          {/* Locations Section - Updated to show 5 locations in a row */}
          <Grid item xs={12} md={5}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  color: '#C6A75E',
                  fontSize: '1rem',
                  letterSpacing: '0.05em',
                }}
              >
                Our Locations
              </Typography>
              <Grid container spacing={1.5}>
                {locations.map((location, index) => (
                  <Grid item xs={12} sm={6} md={4} lg={2.4} key={index}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        backgroundColor: alpha('#C6A75E', 0.05),
                        border: `1px solid ${alpha('#C6A75E', 0.1)}`,
                        transition: 'all 0.3s ease',
                        height: '100%',
                        minHeight: '180px',
                        display: 'flex',
                        flexDirection: 'column',
                        '&:hover': {
                          borderColor: alpha('#C6A75E', 0.3),
                          backgroundColor: alpha('#C6A75E', 0.08),
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography sx={{ fontSize: '1.2rem' }}>{location.flag}</Typography>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#C6A75E' }}>
                          {location.country}
                        </Typography>
                      </Box>
                      
                      {/* Company Name (only for USA) */}
                      {location.companyName && (
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: '#C6A75E', 
                            display: 'block', 
                            mb: 0.5,
                            fontWeight: 500,
                            fontSize: '0.7rem'
                          }}
                        >
                          {location.companyName}
                        </Typography>
                      )}
                      
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1, flex: 1 }}>
                        <LocationOnIcon sx={{ fontSize: 16, color: '#C6A75E', mt: 0.2, flexShrink: 0 }} />
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.4 }}>
                          {location.address.split('\n').map((line, i) => (
                            <React.Fragment key={i}>
                              {line}
                              {i < location.address.split('\n').length - 1 && <br />}
                            </React.Fragment>
                          ))}
                        </Typography>
                      </Box>
                      
                      {/* WhatsApp Icon Only */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <WhatsAppIcon sx={{ fontSize: 16, color: '#25D366' }} />
                        <MuiLink
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handleWhatsAppClick(location.whatsappNumber);
                          }}
                          sx={{
                            color: '#25D366',
                            textDecoration: 'none',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            cursor: 'pointer',
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          WhatsApp
                        </MuiLink>
                      </Box>
                      
                      {/* Email with routing to Concierge */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <EmailIcon sx={{ fontSize: 16, color: '#C6A75E' }} />
                        <MuiLink
                          href="#"
                          onClick={(e) => handleEmailClick(e, location.email)}
                          sx={{
                            color: 'rgba(255,255,255,0.7)',
                            textDecoration: 'none',
                            fontSize: '0.75rem',
                            '&:hover': {
                              color: '#C6A75E',
                            },
                          }}
                        >
                          Email
                        </MuiLink>
                      </Box>
                      
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mt: 1 }}>
                        {location.hours}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </motion.div>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4, backgroundColor: alpha('#C6A75E', 0.2) }} />

        {/* Copyright Section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
            © {currentYear} DiamondCoin. All rights reserved.
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
            All coins certified and authenticated
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <MuiLink href="/terms" sx={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'none', fontSize: '0.7rem' }}>
              Terms
            </MuiLink>
            <MuiLink href="/privacy" sx={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'none', fontSize: '0.7rem' }}>
              Privacy
            </MuiLink>
            <MuiLink href="/sitemap" sx={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'none', fontSize: '0.7rem' }}>
              Sitemap
            </MuiLink>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;